import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { Dispatch, FormEvent, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, applyTableState } from '../../components/DataTable.js';
import type { DataColumn } from '../../components/DataTable.js';
import type { DesktopModelView, TenantAssignment } from '../../types.js';
import { renderCapabilityBadges, renderModelSpec } from '../model-capability-summary.js';
import { modelCustomerLabel } from '../model-customer-label.js';

export interface ModelAssignmentSelection {
  selected: string[];
  selectedDefaults: string[];
  selectedFreeTier: string[];
}

export function assignmentSelectionFrom(
  assignment: TenantAssignment
): ModelAssignmentSelection {
  return {
    selected: assignment.allowedModelIds,
    selectedDefaults: assignment.defaultMemberModelIds ?? [],
    selectedFreeTier: assignment.freeTierModelIds ?? [],
  };
}

export function emptyAssignmentSelection(): ModelAssignmentSelection {
  return { selected: [], selectedDefaults: [], selectedFreeTier: [] };
}

export function normalizeAssignmentSelection(
  selection: ModelAssignmentSelection
): ModelAssignmentSelection {
  return {
    selected: selection.selected,
    selectedDefaults: selection.selectedDefaults.filter((modelId) =>
      selection.selected.includes(modelId)
    ),
    selectedFreeTier: selection.selectedFreeTier.filter((modelId) =>
      selection.selected.includes(modelId)
    ),
  };
}

function toggleModelId(current: string[], modelId: string, checked: boolean): string[] {
  return checked
    ? [...new Set([...current, modelId])]
    : current.filter((id) => id !== modelId);
}

export function toggleAssignedModel(
  selection: ModelAssignmentSelection,
  modelId: string,
  checked: boolean
): ModelAssignmentSelection {
  const selected = toggleModelId(selection.selected, modelId, checked);
  return normalizeAssignmentSelection({ ...selection, selected });
}

export type ModelPolicy = 'access' | 'members' | 'exempt';
export type ModelPolicyFilter = 'all' | ModelPolicy;

const POLICIES: ModelPolicy[] = ['access', 'members', 'exempt'];
const POLICY_KEY: Record<ModelPolicy, keyof ModelAssignmentSelection> = {
  access: 'selected',
  members: 'selectedDefaults',
  exempt: 'selectedFreeTier',
};

export function hasModelPolicy(
  selection: ModelAssignmentSelection,
  modelId: string,
  policy: ModelPolicy
): boolean {
  return selection[POLICY_KEY[policy]].includes(modelId);
}

/** Toggles one policy cell; removing tenant access also clears both dependent policies. */
export function toggleModelPolicy(
  selection: ModelAssignmentSelection,
  modelId: string,
  policy: ModelPolicy,
  checked: boolean
): ModelAssignmentSelection {
  if (policy === 'access') return toggleAssignedModel(selection, modelId, checked);
  const key = POLICY_KEY[policy];
  return { ...selection, [key]: toggleModelId(selection[key], modelId, checked) };
}

export function modelMatchesPolicyFilter(
  selection: ModelAssignmentSelection,
  modelId: string,
  filter: ModelPolicyFilter
): boolean {
  return filter === 'all' || hasModelPolicy(selection, modelId, filter);
}

/** Set-based diff against the last saved tenant response (array order is ignored). */
export function assignmentChangeSummary(
  baseline: ModelAssignmentSelection,
  current: ModelAssignmentSelection
): { models: number; cells: number } {
  const changedModels = new Set<string>();
  let cells = 0;
  for (const policy of POLICIES) {
    const before = new Set(baseline[POLICY_KEY[policy]]);
    const after = new Set(current[POLICY_KEY[policy]]);
    for (const modelId of new Set([...before, ...after])) {
      if (before.has(modelId) === after.has(modelId)) continue;
      changedModels.add(modelId);
      cells += 1;
    }
  }
  return { models: changedModels.size, cells };
}

const POLICY_HELP_KEY: Record<ModelPolicy, string> = {
  access: 'modelAssignment.accessHelp',
  members: 'modelAssignment.tenantDefaultsHelp',
  exempt: 'modelAssignment.freeTierHelp',
};
const PAGE_SIZE = 25;

/** Platform model availability settings: one row per model with the tenant access,
 *  all-member, and credit-exemption policies, saved together. */
export function ModelAssignmentTab({
  tenantId,
  assignment,
  selection,
  setSelection,
  catalog,
  busy,
  onSave,
}: {
  tenantId: string;
  assignment: TenantAssignment;
  selection: ModelAssignmentSelection;
  setSelection: Dispatch<SetStateAction<ModelAssignmentSelection>>;
  catalog: DesktopModelView[];
  busy: boolean;
  onSave: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ModelPolicyFilter>('all');
  const [page, setPage] = useState(0);
  const [clearedNotice, setClearedNotice] = useState('');
  const filterRef = useRef<HTMLSelectElement>(null);
  const idBase = useId();
  const baseline = useMemo(() => assignmentSelectionFrom(assignment), [assignment]);

  // A new saved/loaded baseline makes the previous dependent-clear notice stale.
  useEffect(() => setClearedNotice(''), [assignment]);

  const catalogById = useMemo(() => new Map(catalog.map((model) => [model.id, model])), [catalog]);
  const modelIds = useMemo(
    () => [
      ...new Set([
        ...(assignment.assignableModelIds ?? assignment.allowedModelIds),
        ...assignment.allowedModelIds,
        ...selection.selected,
      ]),
    ],
    [assignment.allowedModelIds, assignment.assignableModelIds, selection.selected]
  );
  const policyLabel: Record<ModelPolicy, string> = {
    access: t('modelAssignment.accessTitle'),
    members: t('modelAssignment.tenantDefaultsTitle'),
    exempt: t('modelAssignment.freeTierTitle'),
  };
  const modelLabel = (modelId: string) => {
    const model = catalogById.get(modelId);
    return model ? modelCustomerLabel(model) : modelId;
  };
  // Screen-reader name: the model ID disambiguates models that share a display name.
  const accessibleModelName = (modelId: string) => {
    const label = modelLabel(modelId);
    return label === modelId ? modelId : `${label} (${modelId})`;
  };

  const editable = Boolean(tenantId.trim()) && !busy;
  const changes = assignmentChangeSummary(baseline, selection);
  const noAccess = selection.selected.length === 0;
  const canSave = editable && changes.cells > 0 && !noAccess;

  function togglePolicy(modelId: string, policy: ModelPolicy, checked: boolean) {
    if (!editable) return;
    const next = toggleModelPolicy(selection, modelId, policy, checked);
    if (policy === 'access' && !checked) {
      const cleared = (['members', 'exempt'] as const).filter((dependent) =>
        hasModelPolicy(selection, modelId, dependent)
      );
      setClearedNotice(
        cleared.length
          ? t('modelAssignment.dependentCleared', {
              model: accessibleModelName(modelId),
              policies: cleared.map((dependent) => policyLabel[dependent]).join(', '),
            })
          : ''
      );
    }
    setSelection((current) => toggleModelPolicy(current, modelId, policy, checked));
    // The row leaves the filtered view, so keep keyboard focus on a stable control.
    if (!modelMatchesPolicyFilter(next, modelId, filter)) filterRef.current?.focus();
  }

  function policyCell(modelId: string, policy: ModelPolicy) {
    const blocked = policy !== 'access' && !hasModelPolicy(selection, modelId, 'access');
    const checked = hasModelPolicy(selection, modelId, policy);
    const paidExempt = policy === 'exempt' && checked && assignment.paidFreeTierModelIds?.includes(modelId);
    const newExempt =
      policy === 'exempt' && checked && !paidExempt && !hasModelPolicy(baseline, modelId, 'exempt');
    const notesId = `${idBase}-${policy}-${encodeURIComponent(modelId)}`;
    const hasNotes = blocked || paidExempt || newExempt;
    return (
      <label className="check-row model-policy-cell">
        <input
          type="checkbox"
          checked={checked}
          disabled={!editable || blocked}
          aria-label={t('modelAssignment.checkboxLabel', {
            model: accessibleModelName(modelId),
            policy: policyLabel[policy],
          })}
          aria-describedby={hasNotes ? notesId : undefined}
          onChange={(event) => togglePolicy(modelId, policy, event.target.checked)}
        />
        <span>
          <strong>{checked ? t('modelAssignment.on') : t('modelAssignment.off')}</strong>
          {hasNotes ? (
            <small id={notesId}>
              {blocked ? t('modelAssignment.requiresAccess') : null}
              {paidExempt ? t('modelAssignment.paidFreeTierWarning') : null}
              {newExempt ? t('modelAssignment.newExemptCost') : null}
            </small>
          ) : null}
        </span>
      </label>
    );
  }

  const columns: DataColumn<string>[] = [
    {
      key: 'model',
      header: t('modelAssignment.modelColumn'),
      render: (modelId) => {
        const model = catalogById.get(modelId);
        return (
          <span className="model-policy-name">
            <strong>{modelLabel(modelId)}</strong>
            {model ? <small>{renderModelSpec(t, model)}</small> : null}
            {model ? renderCapabilityBadges(t, model) : null}
            <small>{modelId}</small>
          </span>
        );
      },
    },
    ...POLICIES.map((policy) => ({
      key: policy,
      header: policyLabel[policy],
      render: (modelId: string) => policyCell(modelId, policy),
    })),
  ];
  const derived = applyTableState(
    modelIds.filter((modelId) => modelMatchesPolicyFilter(selection, modelId, filter)),
    columns,
    { query, searchText: (modelId) => `${catalogById.get(modelId)?.displayName ?? ''} ${modelId}`, page, pageSize: PAGE_SIZE }
  );

  function save(event: FormEvent) {
    event.preventDefault();
    if (canSave) void onSave();
  }

  return (
    <form className="panel model-policy-panel" onSubmit={save}>
      <h2>{t('platform.modelAssignment')}</h2>
      <dl className="model-policy-help">
        {POLICIES.map((policy) => (
          <div key={policy}>
            <dt>{policyLabel[policy]}</dt>
            <dd>{t(POLICY_HELP_KEY[policy])}</dd>
          </div>
        ))}
      </dl>
      <div className="table-toolbar model-policy-toolbar">
        <input
          type="search"
          aria-label={t('modelAssignment.searchPlaceholder')}
          placeholder={t('modelAssignment.searchPlaceholder')}
          value={query}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.preventDefault();
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setPage(0);
          }}
        />
        <label>
          <span>{t('modelAssignment.filterLabel')}</span>
          <select
            ref={filterRef}
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value as ModelPolicyFilter);
              setPage(0);
            }}
          >
            {(['all', ...POLICIES] as const).map((value) => (
              <option key={value} value={value}>
                {t(`modelAssignment.filters.${value}`)}
              </option>
            ))}
          </select>
        </label>
        <span className="table-count">{t('table.rowCount', { count: derived.total })}</span>
      </div>
      {/* Always mounted so assistive technology announces later text changes. */}
      <p className="screen-help model-policy-notice" role="status">
        {clearedNotice}
      </p>
      <DataTable
        rows={derived.pageRows}
        columns={columns}
        empty={modelIds.length ? t('modelAssignment.empty') : t('modelAssignment.noModels')}
        getRowKey={(modelId) => modelId}
        mobileCards
      />
      <div className="table-pager">
        <button type="button" className="ghost" disabled={derived.page <= 0} onClick={() => setPage(derived.page - 1)}>
          {t('table.prev')}
        </button>
        <span>{t('table.pageOf', { page: derived.page + 1, pageCount: derived.pageCount })}</span>
        <button
          type="button"
          className="ghost"
          disabled={derived.page >= derived.pageCount - 1}
          onClick={() => setPage(derived.page + 1)}
        >
          {t('table.next')}
        </button>
      </div>
      <div className="form-actions model-policy-actions">
        <span className="table-count" role="status">
          {noAccess
            ? t('modelAssignment.requireAccessModel')
            : changes.cells
              ? t('modelAssignment.unsavedChanges', changes)
              : t('modelAssignment.noChanges')}
        </span>
        <button disabled={!canSave}>{t('modelAssignment.save')}</button>
      </div>
    </form>
  );
}
